import { Injectable } from '@angular/core';
import { ErrorHandlerService } from './error-handler.service';
import { AuthService } from './auth.service';
import { OrganizationService } from './organization.service';
import Papa from 'papaparse';
import * as Parse from 'parse';

const parseAddress = require('parse-address');

@Injectable()
export class CustomerImportService {

    constructor(
        private errorHandler: ErrorHandlerService,
        private auth: AuthService,
        private orgService: OrganizationService
    ) {
        this.complete = this.complete.bind(this);
    }

    parseCSV(file: File, importRecord: Parse.Object) {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                complete: async (results: Papa.ParseResult<any>) => {
                    const importResults = await this.complete(results, importRecord);
                    resolve(importResults);
                },
                error: reject,
                skipEmptyLines: true,
                transformHeader: (header: string): string => {
                    return header.toLowerCase().trim();
                },
                transform: (value: string): string => {
                    return value.toLowerCase().trim();
                }
            });
        });
    }

    async complete(results: Papa.ParseResult<any>, importRecord: Parse.Object) {
        let duplicates: number = 0;
        let errors: number = 0;

        for (const customerData of results.data) {
            try {
                const customer = this.createCustomer(customerData);
                customer.set('importRecord', importRecord);

                await customer.save();
            } catch (error: any) {
                console.error(error);
                this.errorHandler.handleInvalidSessionError(error);

                if (error.message === 'duplicate-customer') {
                    duplicates++;
                } else {
                    errors++;
                }
            }
        }

        importRecord.set('memberCount', results.data.length);
        await importRecord.save();

        return {
            records: results.data.length,
            duplicates: duplicates,
            errors: errors
        };
    }

    createCustomerImport(source: 'csv' | 'mailchimp' | 'cc', file?: File) {
        const object: Parse.Object = new Parse.Object('CustomerImport');

        if (source === 'csv' && file) {
            const parseFile: Parse.File = new Parse.File((file?.name as string).replace(/[^a-zA-Z0-9]/g,'_'), file as File);
            object.set('csv', parseFile);
        }

        object.set('source', source);

        return object.save();
    }

    createCustomer(customerData: any) {
        const customer: Parse.Object  = new Parse.Object('Customer');

        customer.set('email', this.extractEmail(customerData));
        customer.set('name', this.extractName(customerData));
        customer.set('phone', this.extractPhone(customerData));
        customer.set('preferredAgent', this.auth.currentUser);

        const address = this.extractAddress(customerData);

        if (address) {
            customer.set('zip', address.zip);
            customer.set('street', `${address.number} ${address.street} ${address.type}`.trim());
            customer.set('city', address.city);
            customer.set('state', address.state);
        }

        if (customerData.city) {
            customer.set('city', customerData.city);
        }

        if (customerData.state) {
            customer.set('state', customerData.state);
        }

        if (customerData.zip) {
            customer.set('zip', customerData.zip);
        }

        // Defaults
        customer.set('organization', new Parse.Object('Organization', { objectId: this.orgService.organization.id }));
        // customer.set('roiMin', 0);
        customer.set('regions', []);
        customer.set('deliverSMS', true);
        customer.set('deliverEmail', true);
        customer.set('confirmed', true);

        return customer;
    }

    extractAddress(customerData: any) {
        if (customerData.address) {
            return parseAddress.parseLocation(customerData.address);
        }
    }

    extractName(customerData: any) {
        let name = '';
        if (customerData.name) {
            name = customerData.name;
        } else if (customerData['first name']) {
            name = customerData['first name'];
        }

        if (customerData['last name']) {
            name += ' ' + customerData['last name'];
        }

        return name;
    }

    extractEmail(customerData: any) {
        let email = '';

        if (customerData['email address']) {
            email = customerData['email address'];
        } else if (customerData['email']) {
            email = customerData['email'];
        }

        return email;
    }

    extractPhone(customerData: any) {
        let phone = '';

        if (customerData['phone number']) {
            phone = customerData['phone number'];
        } else if (customerData['phone']) {
            phone = customerData['phone'];
        }

        return phone;
    }

    startConstantContactImport(payload: { code: string, redirectUri: string }) {
        return Parse.Cloud.run('startConstantContactImport', payload);
    }

    mailchimpOauth(payload: { code: string, redirectUri: string }) {
        return Parse.Cloud.run('mailchimpOauth', payload);
    }
}
